// src/pages/AudioTranscriptions.jsx
import React, { useEffect, useMemo, useState } from "react";

const BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

function usd(n) {
  return `$${(n || 0).toFixed(3)}`;
}
// Aproximações de custo
function estimateTranscriptionCost(mins) {
  // ~US$ 0.006 por minuto (Whisper)
  return 0.006 * mins;
}
function estimateTTSCost(chars) {
  // ~US$ 0.015 / 1000 chars (gpt-4o-mini-tts)
  return 0.015 * (chars / 1000);
}

function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function AudioTranscriptions() {
  const [items, setItems] = useState([]);
  const [rawPhone, setRawPhone] = useState("");
  const phone = useDebounced(rawPhone);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Settings (inline, sem api.js)
  const [saving, setSaving] = useState(false);
  const [audioCfg, setAudioCfg] = useState({
    enableTTSResponse: false,
    asrProvider: "openai",
    ttsProvider: "openai",
    defaultVoiceId: "alloy",
  });

  const fetchAudioSettings = async () => {
    try {
      const res = await fetch(`${BASE_URL}/company-profile/settings/audio`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("settings fetch failed");
      const data = await res.json();
      if (data && data.audio) setAudioCfg(data.audio);
    } catch {
      // silencioso para não poluir a tela
    }
  };

  const saveAudioSettings = async (nextCfg) => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/company-profile/settings/audio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(nextCfg),
      });
      if (!res.ok) throw new Error("settings save failed");
      const data = await res.json();
      if (data?.audio) setAudioCfg(data.audio);
    } catch {
      setErr("Failed to save audio settings.");
    } finally {
      setSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const qs = new URLSearchParams({ type: "transcriptions" });
      if (phone) qs.set("phone", phone);
      const res = await fetch(`${BASE_URL}/messages?${qs.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("list fetch failed");
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setErr("Failed to load transcriptions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudioSettings();
    load();
  }, []);
  useEffect(() => {
    load();
  }, [phone]);

  const rows = useMemo(() => {
    return items.map((m) => {
      const meta = m.meta || {};
      const trx = meta.transcription || {};
      const media = meta.media || {};
      const when = m.receivedAt ? new Date(m.receivedAt) : null;
      return {
        id: m._id,
        sender: m.sender,
        whenStr: when ? when.toLocaleString() : "-",
        text: trx.text || m.message || "",
        audioUrl: media.url || "",
        lang: trx.language || "",
        status: trx.status || "",
      };
    });
  }, [items]);

  const minuteExamples = [1, 3, 5];

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-2xl font-bold mb-2">Audio Transcriptions</h1>
      <p className="text-sm text-gray-600 mb-4">
        View inbound audios with their transcribed text. Filter by phone if needed.
      </p>

      {/* Pricing & Mode (inline) */}
      <div className="w-full rounded-xl border border-gray-200 p-4 md:p-6 bg-white shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Pricing & Mode</h2>
            <p className="text-sm text-gray-600">
              Choose how replies are delivered and see rough cost estimates.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={!audioCfg.enableTTSResponse}
                onChange={() =>
                  saveAudioSettings({ ...audioCfg, enableTTSResponse: false })
                }
                disabled={saving}
              />
              <span>Transcribe only</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="mode"
                checked={!!audioCfg.enableTTSResponse}
                onChange={() =>
                  saveAudioSettings({ ...audioCfg, enableTTSResponse: true })
                }
                disabled={saving}
              />
              <span>Cloned voice</span>
            </label>
            <select
              className="border rounded-md px-2 py-1 text-sm"
              value={audioCfg.defaultVoiceId || "alloy"}
              onChange={(e) =>
                saveAudioSettings({ ...audioCfg, defaultVoiceId: e.target.value })
              }
              disabled={saving || !audioCfg.enableTTSResponse}
              title="Default voice"
            >
              <option value="alloy">Alloy</option>
              <option value="verse">Verse</option>
              <option value="aria">Aria</option>
              <option value="coral">Coral</option>
            </select>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg bg-gray-50 p-4">
            <h3 className="font-semibold mb-2">Transcription (approx.)</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              {minuteExamples.map((m) => (
                <li key={m}>
                  {m} min audio → <strong>{usd(estimateTranscriptionCost(m))}</strong>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-2">
              Based on ~$0.006 per minute. Estimates only.
            </p>
          </div>

          <div className="rounded-lg bg-gray-50 p-4">
            <h3 className="font-semibold mb-2">TTS (approx.)</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              {[120, 400, 800].map((chars) => (
                <li key={chars}>
                  {chars} characters → <strong>{usd(estimateTTSCost(chars))}</strong>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-2">
              Based on ~$0.015 per 1,000 chars. Estimates only.
            </p>
          </div>

          <div className="rounded-lg bg-gray-50 p-4">
            <h3 className="font-semibold mb-2">Current providers</h3>
            <p className="text-sm">
              <strong>ASR:</strong> {audioCfg.asrProvider}
            </p>
            <p className="text-sm">
              <strong>TTS:</strong> {audioCfg.ttsProvider}
            </p>
            <p className="text-sm">
              <strong>Voice:</strong> {audioCfg.defaultVoiceId}
            </p>
            {saving && <p className="text-xs text-gray-500 mt-2">Saving…</p>}
          </div>
        </div>
      </div>

      {/* Filtros/Lista */}
      <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
        <div className="flex-1">
          <label className="block text-sm font-medium mb-1">Filter by phone</label>
          <input
            className="w-full border rounded-md px-3 py-2"
            placeholder="+1 555 123 4567"
            value={rawPhone}
            onChange={(e) => setRawPhone(e.target.value)}
          />
        </div>
        <button className="h-10 px-4 rounded-lg bg-gray-900 text-white" onClick={load}>
          Refresh
        </button>
      </div>

      {err && <div className="text-red-600 text-sm mb-3">{err}</div>}
      {loading ? (
        <div className="text-sm text-gray-700">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-700">No transcriptions found.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Sender</th>
                <th className="px-3 py-2">Audio</th>
                <th className="px-3 py-2">Transcription</th>
                <th className="px-3 py-2">Lang</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap">{r.whenStr}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.sender}</td>
                  <td className="px-3 py-2">
                    {r.audioUrl ? (
                      <audio controls src={r.audioUrl} className="max-w-[220px]" />
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="max-w-xl whitespace-pre-wrap break-words">
                      {r.text || <span className="text-gray-500">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">{r.lang || "—"}</td>
                  <td className="px-3 py-2">{r.status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
